import { useEffect, useRef } from "react";
import { playSfx } from "../sound/sfx";
import { sanToCue } from "./chess-motion";

/**
 * The chess sound cues (design spec §12), all bespoke wooden/bell **chess** clips (not the
 * card clips — see `sound/sfx.ts`). Two things happen here:
 *
 *  - A "new game" GONG (`chessStart`) when a FRESH game opens — the table mounts with an
 *    empty move list (a fresh start OR a "play again", which remounts the table). A mid-game
 *    RESUME opens with moves already played, so it stays SILENT (no gong, no replayed move).
 *  - One MOVE cue per completed move, chosen from the move's SAN by {@link sanToCue}: a
 *    wooden tock, a capture knock, the check alert, a castle tap, or a promotion chime.
 *
 * Both solo and online chess render through `ChessTable`, so wiring this ONCE inside that
 * table covers both modes (the pattern the card tables use). Keyed on the move COUNT
 * (`moveList.length`, which only grows on a real move) so a move cue fires exactly once and
 * NEVER replays on a re-render. Unlike the card tables it fires for YOUR OWN move too: a
 * chess move has no in-piece click, so the tap that commits a move is exactly when the
 * place/capture sound belongs. `playSfx` already no-ops when the game sound is off, the
 * table is muted, or the master volume is zero — so this hook is inert in tests and honours
 * every mute.
 */
export function useChessSfx(moveList: readonly string[]): void {
  const prevCount = useRef<number | null>(null);
  const count = moveList.length;
  useEffect(() => {
    const before = prevCount.current;
    prevCount.current = count;
    if (before === null) {
      // First mount: a FRESH game (no moves yet) opens with the wooden "new game" gong;
      // a mid-game resume (moves already played) opens silently.
      if (count === 0) playSfx("chessStart");
      return;
    }
    if (count <= before) return; // no new move (a re-render / an in-place reset)
    const san = moveList[count - 1] ?? "";
    playSfx(sanToCue(san));
  }, [count, moveList]);
}
